<script setup>
import {computed, ref} from "vue";
import CountsBlockNationalities from "./CountsBlockNationalities.vue";
import {mdiHelp, mdiPencilOutline} from "@mdi/js";
import {isEmsMember} from "../utils";
import CountsBlockLegenModal from "./CountsBlockLegenModal.vue";

const props = defineProps({
  hpoId: {
    type: String,
    required: true,
  },
  title: {
    type: String,
    required: false,
    default: pgettext('label', 'START Triage')
  },
  patientCounts: {
    type: Object,
    required: false,
    default: {}
  },
  loading: {
    type: Boolean,
    required: true,
  },
  member: {
    type: Object,
    required: true
  },
  modalComponent: {
    type: Object,
    required: false,
    default: undefined
  },
  hideBlackAndWhite: {
    type: Boolean,
    required: false,
    default: false
  },
  legend: {
    type: String,
    required: false,
    default: null
  },
  showNationalities: {
    type: Boolean,
    required: false,
    default: true
  }
})

const showModal = ref(false)
const showLegendModal = ref(false)

function displayValue(value) {
  return value === null ? '?' : value
}

function sum(obj) {
  const keys = ['red', 'yellow', 'green', 'black', 'uninjured'];
  const values = keys.map(key => obj[key]).filter(val => val !== null);

  if (values.length === 0) {
    return null;
  }

  return values.reduce((sum, val) => sum + val, 0);
}

const subtitle = computed(() => {
  if (props.patientCounts.timestamp && isEmsMember(props.member.role)) {
    return `${pgettext('label', 'Last update')}: ${formatTime(props.patientCounts.timestamp)}`
  }
  return null;
})

</script>

<template>
  <v-card class="hpo-counts-block" :title="title" :subtitle="subtitle">
    <template v-slot:append>
      <v-chip density="compact" rounded variant="flat"
              color="light-blue-lighten-4" class="pa-1"
              @click="showLegendModal=true"
      >
        <v-icon v-if="legend" :icon="mdiHelp"></v-icon>
        <v-tooltip activator="parent" location="left">
          {{ legend }}
        </v-tooltip>
      </v-chip>

    </template>

    <template v-if="patientCounts">
      <div class="hpo-counts-block__body">
        <div class="hpo-counts-block__total">
          {{ pgettext('sum', 'Total: ') }}
          <v-chip color="blue-grey-lighten-4" density="compact" variant="flat" :ripple="false" rounded
                  class="hpo-counts-block__value px-2 ml-2">
            <strong>{{ displayValue(sum(patientCounts)) }}</strong>
          </v-chip>
        </div>
        <div class="hpo-counts-block__values">
          <v-chip color="red" density="compact" variant="flat" :ripple="false" rounded
                  class="hpo-counts-block__value px-2">
            <strong>
              {{ displayValue(patientCounts.red) }}
            </strong>

            <v-tooltip activator="parent" location="top">
              {{ pgettext('label_for_group_of_people', 'Seriously injured') }}
            </v-tooltip>
          </v-chip>
          <v-chip color="yellow" density="compact" variant="flat" :ripple="false" rounded
                  class="hpo-counts-block__value px-2">
            <strong>
              {{ displayValue(patientCounts.yellow) }}
            </strong>

            <v-tooltip activator="parent" location="top">
              {{ pgettext('label_for_group_of_people', 'Moderately injured') }}
            </v-tooltip>
          </v-chip>
          <v-chip color="green" density="compact" variant="flat" :ripple="false" rounded
                  class="hpo-counts-block__value px-2">
            <strong>
              {{ displayValue(patientCounts.green) }}
            </strong>
            <v-tooltip activator="parent" location="top">
              {{ pgettext('label_for_group_of_people', 'Slightly injured') }}
            </v-tooltip>
          </v-chip>
          <template v-if="!hideBlackAndWhite">
            <v-chip v-if="patientCounts.hasOwnProperty('black')"
                    color="black" density="compact" variant="flat" :ripple="false" rounded
                    class="hpo-counts-block__value px-2">
              <strong>
                {{ displayValue(patientCounts.black) }}
              </strong>

              <v-tooltip activator="parent" location="top">
                {{ pgettext('label_for_group_of_people', 'Deceased') }}
              </v-tooltip>
            </v-chip>
            <v-chip v-if="patientCounts.hasOwnProperty('uninjured')"
                    density="compact" variant="outlined" :ripple="false" rounded
                    class="hpo-counts-block__value px-2">
              <strong>
                {{ displayValue(patientCounts.uninjured) }}
              </strong>

              <v-tooltip activator="parent" location="top">
                {{ pgettext('label_for_group_of_people', 'Uninjured') }}
              </v-tooltip>
            </v-chip>
          </template>
        </div>
      </div>
      <CountsBlockNationalities v-if="showNationalities" :data="patientCounts.nationalities"/>
      <v-btn v-if="modalComponent"
             variant="elevated"
             density="compact"
             :prependIcon="mdiPencilOutline"
             block
             @click="showModal=true">
        <span>{{ pgettext('action', 'Edit') }}</span>
      </v-btn>

      <component
        v-if="showModal === true"
        :is="modalComponent"
        :hpoId="hpoId"
        :patientCounts="patientCounts"
        @close="showModal = false"
      />
    </template>
  </v-card>

  <CountsBlockLegenModal v-if="showLegendModal"
                         :patientCounts="patientCounts"
                         :hideBlackAndWhite="hideBlackAndWhite"
                         @close="showLegendModal=false"/>
</template>